/**
 * Temporary: cleans Focus/Error copies left by the pre-868mkgxmj seed. Remove once early-access
 * designs have loaded.
 *
 * The old form-style seed wrote a full copy of Default into `focus` and `error` on every form
 * entry, so every state prop showed as a customized row and none followed Default. This removes,
 * once per theme, each state prop that still holds the seed's value and whose removal changes
 * nothing the storing entry draws for that state on either layout, then marks the theme so it
 * never runs again.
 *
 * The comparison draws the storing entry alone, so it doesn't prove that nothing on the page
 * changes. Two cases can look different on Focus/Error afterwards, both by design, since the copy
 * was never a user choice and the state now follows Default the way a new design's does:
 * - A child that read through `field`'s seeded copy follows its own Default, e.g. dropdown Focus
 *   padding gets its arrow space back.
 * - An element with its own Default override shows that override on Focus/Error, where the theme
 *   copy used to win. Page elements aren't loaded here, so they can't be checked.
 */
import { PropNames } from "@showitinc/merge-utils";
import { isEqual } from "es-toolkit";
import Immutable from "immutable";
import { LAYOUT_DESKTOP, LAYOUT_MOBILE, SYNCED_PROPS } from "@/constants/DesignConstants.mjs";
import {
  FORM_FIELD_STYLE_BY_TYPE,
  FORM_STATE_ERROR,
  FORM_STATE_FOCUS,
  FORM_STYLE_CHECKBOXES,
  FORM_STYLE_DROPDOWN,
  FORM_STYLE_FIELD,
  FORM_STYLE_RADIOS,
} from "@/constants/FormFieldConstants.mjs";
import { createPreTrimFormStyleDefaults } from "@/constants/PreTrimFormStyleDefaults.mjs";
import {
  FORM_FIELD_CHECKBOX_PROPS,
  FORM_FIELD_DROPDOWN_PROPS,
  FORM_FIELD_INPUT_PROPS,
  FORM_FIELD_LABEL_PROPS,
  FORM_FIELD_PROPERTY_META_MAP,
  FORM_FIELD_RADIO_PROPS,
  getFormFieldStateProps,
} from "@/utils/Element/FormFieldPropertyMaps";
import { getRemoveCustomizationUpdates } from "@/utils/FormFieldStateUtils";
import { resolveFormFieldStyles } from "@/utils/FormFieldStyleResolver";
import { applyPropInfoToTheme } from "@/utils/SyncUtils";

/** @typedef {import("immutable").Map} ImmutableMap */
/** @typedef {import("@/types/data/DesignTheme.mjs").DesignTheme} DesignTheme */

const { CONTROL_BORDER_RADIUS, CONTROL_TEXT_FONT, LABEL_FONT } = PropNames;

/**
 * Temporary: cleans Focus/Error copies left by the pre-868mkgxmj seed. Remove once early-access
 * designs have loaded.
 *
 * Top-level theme key (a sibling of `styles`) recording that the reset has run.
 */
export const FORM_STATE_RESET_KEY = "formStateReset";
export const FORM_STATE_RESET_VERSION = 1;

const LAYOUTS = [LAYOUT_DESKTOP, LAYOUT_MOBILE];
const STATES = [FORM_STATE_FOCUS, FORM_STATE_ERROR];

/**
 * Each entry's panel prop list, in cleaning order: `field` first, because a child's state falls
 * back through `field`'s state before its own Default. Radios also clean Corner Radius, which the
 * old seed copied but the panel no longer lists (#1559).
 */
const PANEL_PROPS_BY_STYLE = [
  [FORM_STYLE_FIELD, [...FORM_FIELD_LABEL_PROPS, ...FORM_FIELD_INPUT_PROPS]],
  [FORM_STYLE_DROPDOWN, FORM_FIELD_DROPDOWN_PROPS],
  [FORM_STYLE_RADIOS, [...FORM_FIELD_RADIO_PROPS, CONTROL_BORDER_RADIUS]],
  [FORM_STYLE_CHECKBOXES, FORM_FIELD_CHECKBOX_PROPS],
];

/**
 * A bare element per entry, so the resolver draws the entry's look with nothing overriding it. A
 * real element's own Default override loses to a theme state copy but shows once the copy is
 * gone, so the reset can change that element (see the header).
 */
const ELEMENT_BY_STYLE = Object.fromEntries(
  Object.entries(FORM_FIELD_STYLE_BY_TYPE).map(([type, styleId]) => [
    styleId,
    Immutable.fromJS({ type, style: styleId, desktop: {}, mobile: {} }),
  ]),
);

/**
 * The pre-trim seed, as Immutable. Fonts are left unset: the seed took the design's font, which
 * isn't known now (see `isOldSeedValue`).
 */
const OLD_SEED = Immutable.fromJS(createPreTrimFormStyleDefaults());

const FONT_PROPS = [CONTROL_TEXT_FONT, LABEL_FONT];

/**
 * True when the entry stores exactly what the pre-trim seed wrote for the state prop: the same
 * desktop value (a composite's whole container) and nothing on mobile. Render equality alone can't
 * tell a seeded copy from a user choice that draws the same today, like a palette swatch
 * (`colors-0`) that matches a literal Default. The seed only wrote literals.
 *
 * The seed's font was the design's font when it ran, so a state font counts as seeded when it
 * matches the entry's own desktop Default font. That's a heuristic: Default may have changed since.
 * @param {ImmutableMap} entry - One `theme.styles` form entry.
 * @param {string} styleId
 * @param {string} stateProp - State-addressed prop, e.g. `focus.control.bgcolor`.
 * @returns {boolean}
 */
const isOldSeedValue = (entry, styleId, stateProp) => {
  const path = FORM_FIELD_PROPERTY_META_MAP[stateProp]?.children
    ? stateProp.split(".").slice(0, -1)
    : stateProp.split(".");
  const [, ...basePath] = path;
  const seedValue = FONT_PROPS.includes(basePath.join("."))
    ? entry.getIn([LAYOUT_DESKTOP, ...basePath])
    : OLD_SEED.getIn([styleId, LAYOUT_DESKTOP, ...path]);

  return (
    seedValue !== undefined &&
    Immutable.is(entry.getIn([LAYOUT_DESKTOP, ...path]), seedValue) &&
    entry.getIn([LAYOUT_MOBILE, ...path]) === undefined
  );
};

/**
 * True when the entry draws the state the same in both themes, on desktop and mobile.
 * @param {ImmutableMap} theme
 * @param {ImmutableMap} candidate
 * @param {string} styleId
 * @param {string} state
 * @returns {boolean}
 */
const rendersSame = (theme, candidate, styleId, state) =>
  LAYOUTS.every((layout) => {
    const element = ELEMENT_BY_STYLE[styleId];
    return isEqual(
      resolveFormFieldStyles({ element, theme, layout, state }),
      resolveFormFieldStyles({ element, theme: candidate, layout, state }),
    );
  });

/**
 * True when the entry stores the prop, or any of its composite children, on either layout. Reads
 * each layout as stored, not through sync.
 * @param {ImmutableMap} entry
 * @param {string} propName
 * @returns {boolean}
 */
const isStoredOnEitherLayout = (entry, propName) =>
  [propName, ...(FORM_FIELD_PROPERTY_META_MAP[propName]?.children ?? [])].some((name) =>
    LAYOUTS.some((layout) => entry.getIn([layout, ...name.split(".")]) !== undefined),
  );

/**
 * Drops each Focus/Error `sync` entry the entry stores nothing for, so the prop takes its sync from
 * Default. The old seed listed every state prop, stored or not.
 * @param {ImmutableMap} theme
 * @param {string} styleId
 * @returns {ImmutableMap}
 */
const dropUnstoredStateSync = (theme, styleId) =>
  theme.updateIn(["styles", styleId], (entry) =>
    entry.has(SYNCED_PROPS)
      ? entry.update(SYNCED_PROPS, (sync) =>
          sync.filter(
            (name) => !STATES.includes(name.split(".")[0]) || isStoredOnEitherLayout(entry, name),
          ),
        )
      : entry,
  );

/**
 * Removes one entry's state props that still hold the old seed's value and draw the same without
 * it, then the state `sync` entries left with nothing stored. Each prop is removed the way Remove
 * Customization removes it: both layouts, and a composite's whole container.
 * @param {ImmutableMap} theme
 * @param {string} styleId
 * @param {string[]} panelProps - The entry's panel prop list.
 * @returns {ImmutableMap}
 */
const resetEntry = (theme, styleId, panelProps) =>
  dropUnstoredStateSync(
    STATES.reduce(
      (stateTheme, state) =>
        getFormFieldStateProps(panelProps, state).reduce((current, stateProp) => {
          const entry = current.getIn(["styles", styleId]);
          if (!isOldSeedValue(entry, styleId, stateProp)) return current;

          const candidate = getRemoveCustomizationUpdates({
            entry,
            stateProp,
            metaMap: FORM_FIELD_PROPERTY_META_MAP,
          }).reduce(applyPropInfoToTheme, current);

          return rendersSame(current, candidate, styleId, state) ? candidate : current;
        }, stateTheme),
      theme,
    ),
    styleId,
  );

/**
 * Temporary: cleans Focus/Error copies left by the pre-868mkgxmj seed. Remove once early-access
 * designs have loaded.
 *
 * Runs once per theme: removes each Focus/Error value that still equals the old seed and whose
 * removal changes nothing its entry draws on desktop or mobile, then writes the marker, even when
 * nothing was removed. A marked theme, or one with no form styles yet, comes back untouched.
 * @param {DesignTheme} theme - Plain JS; never mutated.
 * @returns {[DesignTheme, boolean]} The theme, and whether it changed.
 */
export const resetFormFieldStateCopies = (theme) => {
  if (theme[FORM_STATE_RESET_KEY] >= FORM_STATE_RESET_VERSION) return [theme, false];
  if (!PANEL_PROPS_BY_STYLE.some(([styleId]) => theme.styles?.[styleId])) return [theme, false];

  const cleaned = PANEL_PROPS_BY_STYLE.reduce(
    (current, [styleId, panelProps]) =>
      current.hasIn(["styles", styleId]) ? resetEntry(current, styleId, panelProps) : current,
    Immutable.fromJS(theme),
  );

  return [{ ...cleaned.toJS(), [FORM_STATE_RESET_KEY]: FORM_STATE_RESET_VERSION }, true];
};

export default resetFormFieldStateCopies;
